import { Link, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';

import { PERM } from '#/config/permissions';
import { useCan } from '#/modules/infra/auth/useCan';
import { fetchStocktakeDetail } from '#/api/imsMgmt/receipts/stocktake';
import { ROUTES } from '#/config/routes';
import { Button } from '#/modules/commonComponents/button';
import { cn } from '#/lib/utils';
import PageShell from '#/modules/infra/layout/PageShell';
import StocktakeLinesTable from '#/modules/imsMgmt/receipts/stocktake/components/StocktakeLinesTable';
import StocktakeMainInfoCard from '#/modules/imsMgmt/receipts/stocktake/components/StocktakeMainInfoCard';
import { getStocktakeApiErrorMessage } from '#/modules/imsMgmt/receipts/stocktake/helper/apiError';
import { stocktakeDetailQueryKey } from '#/utils/queryKeys';

const StocktakeDetailPage = () => {
  const canUpdate = useCan(PERM.STOCKTAKE_UPDATE);

  const { id } = useParams();

  const detailQuery = useQuery({
    queryKey: stocktakeDetailQueryKey(id),
    queryFn: () => fetchStocktakeDetail(id),
    enabled: Boolean(id),
  });

  const detail = detailQuery.data?.data;
  const isLoading = detailQuery.isLoading;
  const isError = detailQuery.isError;

  return (
    <PageShell variant="detail">
      <div className="layout-shell__body">
        <div className="mx-auto w-full max-w-6xl space-y-4">
          <div
            className={cn(
              'sticky top-0 z-20 -mx-1 flex flex-wrap items-center gap-2',
              'border-b border-slate-200 bg-canvas px-1 pb-3 pt-1',
            )}
          >
            <h1 className="page_title mb-0!">
              <span className="border-l-5 border-primary-400 pl-2.5">盤點單詳情</span>
            </h1>
            <Button type="button" variant="outline" asChild>
              <Link to={ROUTES.stocktake}>返回列表</Link>
            </Button>
            {detail && canUpdate ? (
              <Button type="button" asChild>
                <Link to={ROUTES.stocktakeEdit(detail.id)}>編輯</Link>
              </Button>
            ) : null}
          </div>

          {isLoading ? (
            <p className="text-sm text-slate-500">載入中…</p>
          ) : null}

          {isError ? (
            <p className="text-sm text-destructive">
              {getStocktakeApiErrorMessage(detailQuery.error, '載入盤點單失敗')}
            </p>
          ) : null}

          {detail ? (
            <>
              <StocktakeMainInfoCard detail={detail} />
              <div className="overflow-hidden rounded-md border border-slate-200 bg-panel p-4 shadow-sm">
                <h2 className="mb-3 text-base font-semibold text-gray-800">盤點明細</h2>
                <StocktakeLinesTable lines={detail.lines} />
              </div>
            </>
          ) : null}
        </div>
      </div>
    </PageShell>
  );
};

export default StocktakeDetailPage;
